import { useState } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { IconArrowRight, IconChevronLeft, IconEqual, IconFileText, IconLock } from '@tabler/icons-react';
import { Button, Segmented } from '@hana/ui';
import { PageHeader } from '@hana/shell';
import { fieldCounts, formFields, type FieldState } from './twoForm';
import { rqStatus, shipStatus } from './booking';
import type { BookingRequest } from './types';
import s from './bookingTwoForm.module.css';

// Mint-Booking · "2 form" (spOn): trái = file đọc được, giữa = trạng thái ô, phải = form nhập liệu. Chỉ đọc: "Lưu" / chép ô báo toast qua `onWrite`.

const ST: Record<FieldState, { ic: 'eq' | 'go' | null; cls: string; tip: string }> = {
  eq: { ic: 'eq', cls: 'stOk', tip: 'Khớp với file' },
  diff: { ic: 'go', cls: 'stWarn', tip: 'Lệch · bấm để lấy giá trị file' },
  copy: { ic: 'go', cls: 'stInfo', tip: 'Form trống · bấm để chép từ file' },
  manual: { ic: null, cls: 'stNone', tip: 'Nhập tay' },
  guard: { ic: null, cls: 'stNone', tip: 'Hãng cố định' },
  wait: { ic: null, cls: 'stNone', tip: 'Chưa đọc file' },
};

export function BookingTwoForm({ r, onBack, onWrite }: { r: BookingRequest; onBack: () => void; onWrite: () => void }) {
  const t = useT();
  const [mode, setMode] = useState<'split' | 'form'>('split');
  const [read, setRead] = useState(false);
  const split = mode === 'split';
  const fields = formFields(r, read);
  const c = fieldCounts(fields);
  let num = 0;
  let lastG = '';
  return (
    <div className={s.page}>
      <PageHeader
        title={r.jobId}
        lead={
          <button type="button" className={s.back} aria-label="Về danh sách yêu cầu (Esc)" title="Về danh sách · Esc" onClick={onBack}>
            <IconChevronLeft size={18} aria-hidden="true" />
          </button>
        }
        sub={
          <span className={s.sub}>
            {r.customer} · {r.route} · {r.cont} · {r.carrier} · BookingTeam {r.owner ?? 'chưa nhận'} · <span title="ShipmentStatus">{shipStatus(r).k}</span> · <span title="RQStatus">{rqStatus(r)}</span>
          </span>
        }
        actions={
          <>
            <Segmented
              kind="toggle"
              label="2 form hoặc Form"
              value={mode}
              onChange={(k) => setMode(k as 'split' | 'form')}
              items={[{ key: 'split', label: '2 form' }, { key: 'form', label: 'Form' }]}
            />
            <Button variant="primary" title="Ctrl+S" onClick={onWrite}>{t("bkx.save")}</Button>
          </>
        }
      />
      <div className={s.counts}>
        <span className={s.cnt} title="Form = file"><IconEqual size={13} aria-hidden="true" /> {t("bkx.match")} {c.eq}</span>
        <span className={s.cnt} title="Khác file · bấm → để lấy giá trị file"><IconArrowRight size={13} aria-hidden="true" /> {t("bkx.diff")} {c.diff}</span>
        <span className={s.cnt} title="Form trống mà file đọc được">{t("bkx.copyable")} {c.copy}</span>
        <span className={s.keys}>{t("bkx.keys")}</span>
      </div>
      <section aria-label="2 form" className={s.card}>
        <div className={`${s.row} ${s.head} ${split ? s.split : ''}`}>
          <span>{t("bkx.field")}</span>
          {split && (
            <div className={s.srcBar}>
              <span className={s.fileK}><IconFileText size={14} aria-hidden="true" /> {r.bookingNo ? `BKG_${r.bookingNo}.pdf` : 'Thân thư khách'}</span>
              <Button size="sm" variant="ghost" aria-label="Hana đọc tệp đang chọn" title="Hana chỉ đọc khi bấm · không tự ghi" onClick={() => setRead(true)}>
                {read ? 'Đọc lại' : 'Đọc'}
              </Button>
            </div>
          )}
          {split && <span />}
          <span>Nhập liệu · {r.jobId}</span>
        </div>
        <div className={s.body}>
          {fields.map((f) => {
            const head = f.g !== lastG ? ((lastG = f.g), <div key={`h-${f.g}`} className={s.grp}>{f.g}</div>) : null;
            num += 1;
            const st = ST[f.state];
            const locked = f.ro || f.state === 'guard';
            return (
              <div key={f.key}>
                {head}
                <div className={`${s.row} ${split ? s.split : ''}`}>
                  <span className={s.lab}>
                    <span className={s.num}>{num}</span>
                    {f.k}
                  </span>
                  {split && (
                    <>
                      <div className={`${s.file} ${read && !f.file && f.state !== 'guard' ? s.noFile : ''}`}>{!read && f.state !== 'guard' ? '—' : f.file || (f.state === 'guard' ? f.val : 'file không có')}</div>
                      <button type="button" className={`${s.st} ${s[st.cls]}`} aria-label={`${f.k} · ${st.tip}`} title={`${f.k} · ${st.tip}`} onClick={onWrite}>
                        {st.ic === 'eq' ? <IconEqual size={14} aria-hidden="true" /> : st.ic === 'go' ? <IconArrowRight size={14} aria-hidden="true" /> : null}
                      </button>
                    </>
                  )}
                  <label className={`${s.input} ${locked ? s.ro : ''}`}>
                    <input aria-label={f.k} value={f.val} readOnly placeholder="—" />
                    {locked && <IconLock size={13} aria-hidden="true" />}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
